import { REFS } from 'enums/external-refs'
import { mapActions, mapGetters } from 'vuex'
import { SHOW_ONE_SIGNAL_TEXT_BUS_NAME } from 'enums/oneSignal'

import { COOKIE_ENTITY_TYPE } from 'enums/oneTrust'
import { createOneTrustHandlerInstance } from 'enums/oneTrust/one-trust-handler'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { KEY_CODE } from '@fmpedia/enums'
import { ROUTE_NAME } from '../enums/routes'

const ONE_SIGNAL_BELL_CONTAINER_ID = 'onesignal-bell-container'
const ONE_SIGNAL_BELL_LAUNCHER_BUTTON_CLASS = 'onesignal-bell-launcher-button'
const ROUTE_NAMES_WITH_ONESIGNAL = [ROUTE_NAME.LIVE_FEED]

/**
 * At first, this variable was located in "data", but when navigating between
 * layouts (default -> error or vice versa), the mixin got destroyed and so was
 * the saved container. To fix it we moved this variable to the outer scope
 */
let oneSignalNativeElementSaved = null

function executeClickHandlerOnEnter(event, el) {
  // Check if the Enter key was pressed
  if (event.key === 'Enter' || event.keyCode === KEY_CODE.ENTER) {
    // Prevent the default action to avoid any unwanted behavior
    event.preventDefault()

    // Programmatically trigger a click event on the element
    el.click()
  }
}

function makeElementFocusable(el) {
  if (!el) return

  el.setAttribute('tabIndex', '0')

  el.addEventListener('keydown', event => executeClickHandlerOnEnter(event, el))
}

/**
 * OneSignal
 * @link https://app.onesignal.com/apps/e262fc34-b784-4b2f-9596-d0cffc046c7e/settings
 */
export const oneSignal = {
  mixins: [hydrationHelpers],
  computed: {
    ...mapGetters({
      isPreviewMode: 'isPreviewMode'
    }),
    shouldShowOneSignal() {
      return (
        ROUTE_NAMES_WITH_ONESIGNAL.includes(this.$route.name) &&
        !this.isPreviewMode
      )
    }
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        if (!process.client) {
          return
        }

        this.$_oneSignal_initializeOneSignal()
      }
    }
  },
  methods: {
    ...mapActions({
      registerHandlerThatRequiresConsent:
        'one-trust/registerHandlerThatRequiresConsent'
    }),
    $_oneSignal_initializeOneSignal() {
      this.registerHandlerThatRequiresConsent(
        createOneTrustHandlerInstance({
          handler: this.$_oneSignal_init,
          entityType: COOKIE_ENTITY_TYPE.ONE_SIGNAL
        })
      )
    },
    $_oneSignal_getTargetContainerForNativeOneSignalElement() {
      return this.$helper.getElByDataRef(REFS.ONE_SIGNAL_BUTTON)
    },
    async $_oneSignal_showOneSignalIcon() {
      const isSubscribed = await window.OneSignal.getSubscription()
      this.$bus.$emit(SHOW_ONE_SIGNAL_TEXT_BUS_NAME, isSubscribed)
    },
    $_oneSignal_getOneSignalNativeElement() {
      return (
        oneSignalNativeElementSaved ||
        document.getElementById(ONE_SIGNAL_BELL_CONTAINER_ID)
      )
    },
    async $_oneSignal_relocateOneSignalToCustomElement() {
      if (!this.shouldShowOneSignal) {
        // to prevent it from being hoverable on pages where it shouldn't be displayed
        this.$_oneSignal_hideNativeIcon()
        return
      }

      const oneSignalNativeElement = this.$_oneSignal_getOneSignalNativeElement()

      const targetContainerForOneSignalElement = this.$_oneSignal_getTargetContainerForNativeOneSignalElement()

      if (!oneSignalNativeElement || !targetContainerForOneSignalElement) {
        return
      }

      // in case it was previously hidden
      this.$_oneSignal_showNativeIcon()

      const isNativeOneSignalElementAlreadyInContainer = !!targetContainerForOneSignalElement.querySelector(
        `#${ONE_SIGNAL_BELL_CONTAINER_ID}`
      )

      if (isNativeOneSignalElementAlreadyInContainer) return

      if (!oneSignalNativeElementSaved) {
        oneSignalNativeElementSaved = oneSignalNativeElement

        makeElementFocusable(
          oneSignalNativeElement.querySelector(
            `.${ONE_SIGNAL_BELL_LAUNCHER_BUTTON_CLASS}`
          )
        )
      }

      await this.$_oneSignal_showOneSignalIcon()

      targetContainerForOneSignalElement.appendChild(oneSignalNativeElement)
    },
    $_oneSignal_addOneSignalToHead() {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.onload = this.$_oneSignal_openOneSignal
      script.src = 'https://cdn.onesignal.com/sdks/OneSignalSDK.js'
      this.$helper.insertScriptToHead(script)
    },
    $_oneSignal_openOneSignal() {
      const oneSignal = window.OneSignal || {}
      const appId = this.$env.IL_ONE_SIGNAL_ID
      const ctx = this

      /**
       * For dev environment you should use ngrok for forwarding your localhost
       * to external https address (add this address to OneSignal configuration)
       * @type {string}
       */
      oneSignal.push(function() {
        oneSignal.init({
          appId
        })

        oneSignal.on('subscriptionChange', isSubscribed => {
          ctx.$bus.$emit(SHOW_ONE_SIGNAL_TEXT_BUS_NAME, isSubscribed)
        })

        /**
         * Since there are no explicit API methods/events for defining the completion
         * of the OneSignal initialization, this method is used for such a purpose.
         * We also need to make sure the OneSignal's container is rendered before trying
         * to relocate it. If cache is disabled in the browser, the time when this container
         * is ready is arbitrary, that's why we use polling.
         */
        oneSignal.getTags().then(() =>
          ctx.$helper.pollUntil({
            fn: ctx.$_oneSignal_relocateOneSignalToCustomElement,
            condition: () =>
              !!document.getElementById(ONE_SIGNAL_BELL_CONTAINER_ID)
          })
        )
      })
    },
    $_oneSignal_hideNativeIcon() {
      const oneSignalNativeElement = this.$_oneSignal_getOneSignalNativeElement()
      oneSignalNativeElement.style.display = 'none'
    },
    $_oneSignal_showNativeIcon() {
      const oneSignalNativeElement = this.$_oneSignal_getOneSignalNativeElement()
      oneSignalNativeElement.style.display = 'block'
    },
    $_oneSignal_init() {
      if (!window.OneSignal) {
        this.$_oneSignal_addOneSignalToHead()
      }

      if (!this.shouldShowOneSignal) return

      requestAnimationFrame(this.$_oneSignal_relocateOneSignalToCustomElement)
    }
  }
}
